import { Modal } from "flowbite-react";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { FormikProvider, useFormik } from "formik";
import { useEffect, useState } from "react";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import InputFormik from "../../../../common/components/form/InputFormik";
import "./ModalCreatePurchaseOrder.scss";
import ComboBoxFormik from "../../../../common/components/form/ComboBoxFormik";
import { getAllCategories } from "../../../../common/services/purchaseCategory.api";
import { IPurchaseCategory } from "../../../../types/purchaseCategory.type";
import { emptyPurchaseOrder, IPurchaseOrder, TYPE_PAYMENT } from "../../../../types/purchaseOrder.types";
import { getAllPurchaseRequestors } from "../../../../common/services/purchaseRequestor.api";
import { IPurchaseRequestor } from "../../../../types/purchaseRequestor.type";
import FormItens from "../form/FormItens";
import * as Yup from "yup";
import { maskCurrency } from "../../../../common/helpers/mask.helpers";
import { attachBilletFileInSchedulePayment, createPurchaseOrder, uploadFilePresignedUrl } from "../../../../common/services/purchaseOrder.api";
import { useUser } from "../../../../common/services/users.api";
import TextareaFormik from "../../../renters/components/forms/TextareaFormik";
import AutocompleteFormik from "../../../../common/components/form/AutocompleteFormik";
import ModalAlert from "./ModalAlert";
import { getCode } from "@/lib/utils";
import { useGetCostCenter } from "../../hooks";
import { useGetAllSuppliers } from "@/common/hooks";
import { FileInput } from "../../../../common/components/form/FileInput";
import { Link } from "react-router-dom";
import { ISupplier } from "../../../../types/supplier.types";

type Props = {
  onClose?: () => void
}

/** CPF (11 dígitos) ou CNPJ (14 dígitos) considerado preenchido. */
function supplierHasTaxIdFilled(cnpj: unknown): boolean {
  if (cnpj == null) return false;
  const digits = String(cnpj).replace(/\D/g, "");
  return digits.length === 11 || digits.length === 14;
}

enum TYPE_ALERT {
  NORMAL = 'NORMAL',
  PRICE = 'PRICE'
}

function isTodayOrFuture(dateString) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const inputDate = new Date(dateString + 'T00:00:00');
  inputDate.setHours(0, 0, 0, 0);

  return inputDate >= today;
}

const createSchema = () => {

  const componentsPieceLength = window.document.querySelectorAll('#componentProduct').length;
  const componentsInstalmentLength = window.document.querySelectorAll('#instalment').length;

  const objValidationComponentsPiece = {
    financial: Yup.object().shape({
      costCenterId: Yup.string().required("Centro de Custo é obrigatório"),
      categoryId: Yup.string().required("Categoria é obrigatório"),
      expectedDate: Yup.string().required('Data da parcela é obrigatório').test('nextDueDate',
        'A data não pode ser anterior a hoje.', value => isTodayOrFuture(value)),

      typePayment: Yup.string().required("Tipo de pagamento é obrigatório"),
      transfer: Yup.object().shape({
        name: Yup.string().test(
          'validate-name',
          'Nome é obrigatório',
          function (value) {
            const { typePayment } = this.parent;
            if (typePayment === 'transferencia' && !value) {
              return false;
            }
            return true;
          }
        ),
        agency: Yup.string().test(
          'validate-agency',
          'Agencia é obrigatório',
          function (value) {
            const { typePayment } = this.parent;
            if (typePayment === 'transferencia' && !value) {
              return false;
            }
            return true;
          }
        ),
        accountBank: Yup.string().test(
          'validate-accountBank',
          'Conta bancária é obrigatório',
          function (value) {
            const { typePayment } = this.parent;
            if (typePayment === 'transferencia' && !value) {
              return false;
            }
            return true;
          }
        )
      }),
      pixKey: Yup.string().test(
        'valida-pix',
        'Chave PIX é obrigatório',
        function (value) {
          const { typePayment } = this.parent;
          if (typePayment === 'pix' && !value) {
            return false;
          }
          return true;
        }
      ),
      checkNumber: Yup.string().test(
        'validate-checkNumber',
        'Nº Cheque é obrigatório',
        function (value) {
          const { typePayment } = this.parent;
          if (typePayment === 'cheque' && !value) {
            return false;
          }
          return true;
        }
      ),
      qtdInstalment: Yup.number().required("Parcela é obrigatória").test('valida-qtd', 'Valor tem que ser maior que 0', value => value > 0),
    })
  };

  new Array(componentsPieceLength).fill(null).map((_, i) => {
    objValidationComponentsPiece[`item${i}`] = Yup.mixed().required('Item é obrigatório');
    objValidationComponentsPiece[`qtd${i}`] = Yup.number().required('Quantidade do item é obrigatório')
      .test('valida-qtd', 'Quantidade inválido', value => value > 0);
    objValidationComponentsPiece[`value${i}`] = Yup.string().required('Valor é obrigatório')
      .test('valida-value', 'Valor tem que ser maior que 0', value => parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.')) > 0)
  });

  new Array(componentsInstalmentLength).fill(null).map((_, i) => {
    objValidationComponentsPiece[`expectedDate${i}`] = Yup.string().required('Data da parcela é obrigatório').test('nextDueDate',
      'A data não pode ser anterior a hoje.', value => isTodayOrFuture(value))
  });

  return Yup.object().shape(objValidationComponentsPiece);
}

export default function ModalCreatePurchaseOrder(props: Props) {
  const { notifySuccess, notifyError, notifyInfo } = useToastContext();
  const [loading, setLoading] = useState(false);
  const handleCloseModal = () => { props.onClose && props.onClose() };

  const [categories, setCategories] = useState<IPurchaseCategory[]>([]);
  const [requestors, setRequestors] = useState<IPurchaseRequestor[]>([]);
  const [qtdInstalment, setQtdInstalment] = useState(1);
  const [typeAlert, setTypeAlert] = useState<TYPE_ALERT>();
  const [showSupplierCnpjHint, setShowSupplierCnpjHint] = useState(false);

  const initialValues = { ...emptyPurchaseOrder() };
  const { data: costCenters = [] } = useGetCostCenter()
  const { data: suppliers = [] } = useGetAllSuppliers();

  const user = useUser();
  function fileArrayToFileList(files: File[]): FileList {
    const dataTransfer = new DataTransfer();
    files.forEach(file => dataTransfer.items.add(file));
    return dataTransfer.files;
  }

  useEffect(() => {
    async function loadRequestors() {
      const result = await getAllPurchaseRequestors();
      setRequestors(result.data);
    }

    async function loadAll() {
      await loadRequestors();
    }

    loadAll();
  }, []);

  const verifyComplementaryPaymentType = (data: IPurchaseOrder) => {

    if (data.financial.typePayment != TYPE_PAYMENT.Transferencia) {
      delete data.financial.transfer
    }
    if (data.financial.typePayment != TYPE_PAYMENT.Cheque) {
      delete data.financial.checkNumber
    }
    if (data.financial.typePayment != TYPE_PAYMENT.Pix) {
      delete data.financial.pixKey
    }
  }

  const formik = useFormik({
    initialValues,
    onSubmit: async (data) => {
      setLoading(true);

      try {
        const supplierSel = data.supplier as { value?: string } | null | "";
        const supplierId =
          supplierSel && typeof supplierSel === "object"
            ? supplierSel.value
            : undefined;
        const supplierRow = suppliers.find((s: ISupplier) => s._id === supplierId);
        if (!supplierId || !supplierRow || !supplierHasTaxIdFilled(supplierRow.cnpj)) {
          setShowSupplierCnpjHint(true);
          notifyError(
            "Fornecedor sem CPF/CNPJ cadastrado. Regularize o cadastro em Fornecedores antes de criar a ordem."
          );
          return;
        }
        setShowSupplierCnpjHint(false);

        setFormItensFormik();
        setInstalmentFormik();

        verifyComplementaryPaymentType(data);
        const requestor = requestors.find(requestor => requestor.employeeEmail == user.email);
        if (!requestor) {
          notifyInfo(`Usuário: ${user.email} não é solicitante de Ordem de Compra`);
          return;
        }
        const code = getCode();

        const
          allValueParcel = Number(data.financial.instalment.reduce((acc, item) => item.value + acc, 0).toFixed(2));
        if (allValueParcel != data.financial.value) {
          notifyError(`A soma não confere valor das parcelas: ${allValueParcel}, valor da Compra: ${data.financial.value}`);
          return;
        }

        if (!typeAlert) {
          setTypeAlert(TYPE_ALERT.NORMAL);
          return;
        }

        const billetFiles = data.financial.billetFiles ? Array.from(data.financial.billetFiles as FileList) : [];
        const nfFiles = data.financial.nfFiles ? Array.from(data.financial.nfFiles as FileList) : [];
        const allFiles = [...billetFiles, ...nfFiles];

        const financialData = { ...data.financial };
        if (billetFiles.length > 0) {
          financialData.billetFiles = billetFiles.map(file => file.name).join(',');
        } else {
          delete financialData.billetFiles;
        }
        if (nfFiles.length > 0) {
          financialData.nfFiles = nfFiles.map(file => file.name).join(',');
        } else {
          delete financialData.nfFiles;
        }

        const resultSendFiles = await attachBilletFileInSchedulePayment({
          ...data,
          requestorId: requestor?._id as string,
          requestorName: requestor?.name as string,
          requestorEmail: requestor?.employeeEmail as string,
          financial: financialData,
          code: `${code}`,
        }, fileArrayToFileList(
          Array.from(allFiles).map((file) => {
            const newFileName = `${file.name}`;
            return new File([file], newFileName, { type: file.type });
          })
        ));

        if (!resultSendFiles?.ok) {
          notifyError(resultSendFiles?.message || "Ocorreu um erro ao preparar os arquivos da ordem de compra!");
          return;
        }

        const result = await createPurchaseOrder({
          ...data,
          isCounted: verifyHasCounted(),
          deliveryDate: verifyHasCounted() ? new Date() : undefined,
          disproportionatePrice: typeAlert == TYPE_ALERT.PRICE ? [{}] : undefined,
          requestorId: requestor?._id as string,
          requestorName: requestor?.name as string,
          requestorEmail: requestor?.employeeEmail as string,
          financial: financialData,
          code: `${code}`,
        });

        if (!result.ok && (result.code === 409 || result.code === 401) && typeAlert != TYPE_ALERT.PRICE) {
          setTypeAlert(TYPE_ALERT.PRICE);
          return;
        }

        if (!result.ok) {
          notifyError(result.message || "Ocorreu um erro ao salvar a ordem de compra!");
          return;
        }

        if (resultSendFiles?.data?.isPresigned && resultSendFiles?.data?.urls) {
          for (const file of allFiles) {
            const urlInfo = resultSendFiles.data.urls.find(url => url.name === file.name);

            if (!urlInfo) {
              notifyError(`Não foi possivel enviar o arquivo: ${file.name}`);
              return;
            }
            try {
              await uploadFilePresignedUrl(file, urlInfo.url);
            } catch (uploadError) {
              notifyError(`Failed to upload file: ${file.name}`);
              return;
            }
          }
        }

        notifySuccess("Criado Ordem de Compra com Sucesso.");
        handleCloseModal();
        setTypeAlert(undefined);
      } catch (error) {
        console.error("Erro ao criar ordem de compra:", error);
        notifyError("Ocorreu um erro ao salvar a ordem de compra!");
      } finally {
        setLoading(false);
      }
    },
    validationSchema: createSchema()
  });

  const verifyHasCounted = () => {
    return Object.keys(formik.values).filter(key => key.startsWith('isCounted')).map(key => formik.values[key]).filter(value => value).length > 0
  }

  const setInstalmentFormik = () => {

    const instalment = new Array(Number(formik.values.financial.qtdInstalment)).fill(null).map((_, i) => {
      return {
        installmentNumber: `${i + 1}`,
        value: parseMoneyStringToNumber(formik.values[`expectedDateValue${i}`]),
        dueDate: formik.values[`expectedDate${i}`]
      }
    });

    formik.values.financial.instalment = instalment;
  }

  const parseMoneyStringToNumber = (value) => {
    if (typeof value == 'number') {
      return value
    }
    if (!value) {
      return 0;
    }
    return parseFloat(value.replace("R$", "").replace(/\s/g, "").replace(/\./g, "").replace(",", "."));
  }


  const setFormItensFormik = () => {
    const componentsPieceLength = window.document.querySelectorAll('#componentProduct').length;

    let totalValue = 0;
    const updatedItens = new Array(Number(componentsPieceLength)).fill(null).map((_, index) => {

      const item = formik.values[`item${index}`];
      const description = formik.values[`isCounted${index}`] ? (item.description ? item.description : item.label) : item;
      const productId = formik.values[`isCounted${index}`] ? item.value : '';
      const qtd = formik.values[`qtd${index}`];
      const value = formik.values[`value${index}`];
      totalValue += (
        parseMoneyStringToNumber(value) * Number(qtd)
      );

      if (productId) {
        return {
          description,
          qtd,
          value,
          productId
        }
      }
      return {
        description,
        qtd,
        value,
      }
    });

    const supplier = formik.values.supplier as unknown as { label: string, value: string };
    formik.values.supplierName = supplier.label;
    formik.values.supplierId = supplier.value;

    formik.values.itens = updatedItens;
    formik.values.financial.value = Number((
      totalValue + parseMoneyStringToNumber(formik.values.financial.valueOfLabor)
      + parseMoneyStringToNumber(formik.values.financial.deliveryForecast) + parseMoneyStringToNumber(formik.values.financial.taxes)
      - parseMoneyStringToNumber(formik.values.financial.discountAmount)).toFixed(2));

    formik.values.financial.taxes = parseMoneyStringToNumber(formik.values.financial.taxes);
    formik.values.status = "Aguardando Aprovação";
  };

  useEffect(() => {

    async function loadCategories() {
      const costCenter = costCenters?.find(costCenter => costCenter.costCenterId == formik.values.financial.costCenterId);
      const result = (await getAllCategories()).data.filter(cateogry => costCenter?.categoriesId.includes(cateogry._id));
      setCategories(result);
    }
    loadCategories();
  }, [costCenters, formik.values.financial.costCenterId]);

  return (
    <>
      <Modal
        className='relative text-black'
        show={true}
        onClose={() => handleCloseModal()}
      >
        <Modal.Header className='text-black dark:text-black'>
          <h2 className="text-black">Nova Ordem de Compra</h2>
          <div className="mt-3 flex items-start gap-3 p-3 bg-yellow-50 text-yellow-800 rounded-md border border-yellow-100">
            <div className="text-sm leading-tight">
              As compras de peças devem ser solicitadas somente via Requisições de Compras.
              <button
                type="button"
                onClick={() => window.location.assign('/compras/requestordem')}
                className="ml-2 inline-block text-sm font-semibold underline text-yellow-700 hover:text-yellow-900 focus:outline-none focus:ring-2 focus:ring-yellow-200 rounded"
                aria-label="Ir para Requisições de Compras"
              >
                Clique aqui
              </button>
              <span className="ml-1 text-gray-600">para abrir a tela de requisições.</span>
            </div>
          </div>

        </Modal.Header>
        <Modal.Body className='text-black dark:text-black' >
          <ModalAlert handleClose={() => setTypeAlert(undefined)} type={typeAlert}></ModalAlert>
          <form onSubmit={formik.handleSubmit}>
            <FormikProvider value={formik}>

              <AutocompleteFormik
                label="Fornecedor:"
                name="supplier"
                required={true}
                options={[{ label: '', value: '' }, ...suppliers.map(supplier => ({ value: supplier._id, label: supplier.companyName }))]}
                allowNewItem={false}
                className='w-small mt-1'
                onValueChange={(newValue) => {
                  setShowSupplierCnpjHint(false);
                  if (
                    !newValue ||
                    typeof newValue !== "object" ||
                    !("value" in newValue)
                  ) {
                    return;
                  }
                  const opt = newValue as { value: string; label: string };
                  if (!opt.value) return;
                  const row = suppliers.find((s: ISupplier) => s._id === opt.value);
                  if (!row || supplierHasTaxIdFilled(row.cnpj)) return;
                  setShowSupplierCnpjHint(true);
                  notifyError(
                    "Este fornecedor não possui CPF/CNPJ cadastrado. Atualize o cadastro em Fornecedores."
                  );
                  formik.setFieldValue("supplier", "");
                }}
              />
              {showSupplierCnpjHint && (
                <div
                  className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
                  role="alert"
                >
                  <p className="m-0 mb-2">
                    É obrigatório informar o CPF ou CNPJ do fornecedor para
                    criar a ordem de compra.
                  </p>
                  <Link
                    to="/fornecedores"
                    className="font-semibold text-redPrimary underline hover:no-underline"
                  >
                    Abrir cadastro de fornecedores
                  </Link>
                </div>
              )}

              <ComboBoxFormik
                className="mt-2"
                name="financial.costCenterId"
                options={[{ label: '', value: '' }, ...costCenters.map(costCenter => ({ value: costCenter.costCenterId, label: costCenter.description }))]}
                label="Selecione o Centro Custo:"
                onChange={value => {
                  const costCenterName = costCenters.find(costCenter => costCenter.costCenterId === value)?.description;
                  formik.setFieldValue('financial.costCenterName', costCenterName);
                }}
                required
              />
              <ComboBoxFormik
                className="mt-2"
                name="financial.categoryId"
                options={[{ label: '', value: '' }, ...categories.map(category => ({ value: category.idNibo, label: category.name }))]}
                label="Selecione a Categoria:"
                onChange={value => {
                  const categoryName = categories.find(category => category.idNibo === value)?.name;
                  formik.setFieldValue('financial.categoryName', categoryName);
                }}
                required
              ></ComboBoxFormik>
              <InputFormik className="mt-2" label="Data de pagamento previsto:" name="financial.expectedDate" type="date" required></InputFormik>
              <FormItens />
              <div className="mt-2">
                <FileInput
                  nameFile="Anexar Boleto(s)"
                  name="financial.billetFiles"
                />
              </div>
              <div className="mt-2">
                <FileInput
                  nameFile="Anexar Nota(s) Fiscal(is)"
                  name="financial.nfFiles"
                />
              </div>
              <ComboBoxFormik label="Tipo de pagamento" name="financial.typePayment"
                options={[
                  { label: '', value: '' },
                  { label: TYPE_PAYMENT.Boleto, value: TYPE_PAYMENT.Boleto },
                  { label: TYPE_PAYMENT.Pix, value: TYPE_PAYMENT.Pix },
                  { label: TYPE_PAYMENT.Transferencia, value: TYPE_PAYMENT.Transferencia },
                  { label: TYPE_PAYMENT.Credito, value: TYPE_PAYMENT.Credito },
                  { label: TYPE_PAYMENT.Dinheiro, value: TYPE_PAYMENT.Dinheiro },
                  { label: TYPE_PAYMENT.Cheque, value: TYPE_PAYMENT.Cheque }
                ]}
              >
              </ComboBoxFormik>
              {formik.values.financial.typePayment === 'transferencia' && (
                <div className="ml-4 mb-5">
                  <h3 className="mb-4 text-transferencia">Dados do Beneficiário</h3>
                  <InputFormik type="text" label="Nome completo:" name="financial.transfer.name" required />
                  <InputFormik className="mt-2" label="Agência:" name="financial.transfer.agency" required />
                  <InputFormik className="mt-2" type="text" label="Conta bancária:" name="financial.transfer.accountBank" required />
                </div>
              )}
              {formik.values.financial.typePayment === 'pix' && (
                <div className="ml-4 mb-5">
                  <InputFormik type="text" label="Chave Pix:" name="financial.pixKey" required />
                </div>
              )}
              {formik.values.financial.typePayment === 'cheque' && (
                <div className="ml-4 mb-5">
                  <InputFormik type="text" label="Nº Cheque:" name="financial.checkNumber" required />
                </div>
              )}
              <InputFormik className="mt-2" label="Valor de Desconto:" name="financial.discountAmount" mask={maskCurrency}></InputFormik>
              <InputFormik className="mt-2" label="Valor de Mão de Obra:" name="financial.valueOfLabor" mask={maskCurrency}></InputFormik>
              <InputFormik className="mt-2" label="Valor de Entrega:" name="financial.deliveryForecast" mask={maskCurrency}></InputFormik>
              <InputFormik className="mt-2" onChange={el => setQtdInstalment(Number(el.target.value))} label="Número de Parcelas" name="financial.qtdInstalment" type="number" required></InputFormik>
              <InputFormik className="mt-2 mb-4" label="Valor de Impostos:" name="financial.taxes" mask={maskCurrency}></InputFormik>
              {new Array(qtdInstalment).fill(null).map((_, i) => {
                return (
                  <div id="instalment">
                    <InputFormik className="mt-2" label={`Data da parcela ${i + 1}º`} name={`expectedDate${i}`} type="date" required></InputFormik>
                    <InputFormik className="mt-2" label={`Valor da parcela ${i + 1}º`} name={`expectedDateValue${i}`}
                      mask={maskCurrency}
                      required={true}></InputFormik>
                  </div>
                )
              })}

              <TextareaFormik
                label={`Observação`}
                placeholder="Digite observações da Compra..."
                name='observations'
                className="min-h-64 mt-4 w-full rounded shadow-md focus:border-white"
              />


            </FormikProvider>
            <div className="containerButtonPurcahseOrder">
              <ButtonWithLoader id="buttonSend" disabled={Object.keys(formik.errors).length > 0 ? true : false} type="submit" isLoading={loading} >Cadastrar</ButtonWithLoader>
            </div>
          </form>
        </Modal.Body>
      </Modal >
    </>
  );
}
